/** @jsxImportSource @emotion/react */

import { FC, useEffect, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { ApolloError, useMutation, useQuery } from '@apollo/client'
import { Button } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { groupBy } from 'lodash'
import {
  updateUserMembershipByAdmin,
  updateUserMembershipByAdminVariables,
} from '__generatedTypes__/updateUserMembershipByAdmin'
import { findOneUser_findOneUser as User } from '__generatedTypes__/findOneUser'
import Loader from 'components/Loader'
import { GET_MEMBERSHIP_LOG, UPDATE_USER_MEMBERSHIP_BY_ADMIN } from 'queries/users'
import { findAllMembershipLog, findAllMembershipLogVariables } from '__generatedTypes__/findAllMembershipLog'
import Table from 'components/Table'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { MembershipInput, MembershipType, PlatformName, ProductTypeEnum } from '__generatedTypes__/globalTypes'
import { findAllPlatforms, findAllPlatformsVariables } from '__generatedTypes__/findAllPlatforms'
import { GET_PLATFORMS } from 'queries/common'
import { toast } from 'utils/toast'
import { useAuthContext } from 'utils/auth'
import { findAllProduct, findAllProductVariables } from '__generatedTypes__/findAllProduct'
import { FIND_ALL_PRODUCT } from 'queries/products'
import * as tabsClasses from '../styles'
import * as classes from './styles'
import { MembershipForm, MembershipFormItem, SelectedPlatform } from './MembershipForm'

type Props = {
  user: User
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchUser: () => Promise<any>
}

export const Memberships: FC<Props> = ({ user, refetchUser }) => {
  const { isAdmin, isMarketingTeamMember } = useAuthContext()
  const [updateUserMembershipByAdmin, { loading: updatingUserMembership }] = useMutation<
    updateUserMembershipByAdmin,
    updateUserMembershipByAdminVariables
  >(UPDATE_USER_MEMBERSHIP_BY_ADMIN)

  const { data: platformsData, loading: platformsLoading } = useQuery<findAllPlatforms, findAllPlatformsVariables>(
    GET_PLATFORMS,
    {
      variables: { query: { take: TAKE_ALL_ITEMS_COUNT } },
    }
  )
  const { data: productsData, loading: productsLoading } = useQuery<findAllProduct, findAllProductVariables>(
    FIND_ALL_PRODUCT,
    {
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
        },
        withHidden: true,
      },
    }
  )

  const platforms = platformsData?.findAllPlatforms.items || []
  const { id: userId, membership } = user
  const nonFreeMemberships = useMemo(() => membership.filter(({ type }) => type !== MembershipType.Free), [membership])

  const products = useMemo(() => {
    const nonFreeProducts = productsData?.findAllProduct.items?.filter((product) =>
      [ProductTypeEnum.Premium, ProductTypeEnum.PremiumMax].includes(product.type)
    )

    if (!nonFreeProducts?.length) {
      return {}
    }

    return groupBy(nonFreeProducts, (product) => product.platform.title)
  }, [productsData])

  const [membershipForms, setMembershipForms] = useState<MembershipFormItem[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<SelectedPlatform[]>([])

  useEffect(() => {
    setMembershipForms(nonFreeMemberships.map((membership) => ({ ...membership, isNew: false })))
    setSelectedPlatforms(nonFreeMemberships.map(({ platform }) => platform.title))
  }, [nonFreeMemberships])

  const {
    data: membershipData,
    loading: membershipLoading,
    refetch: refetchMembershipLogs,
  } = useQuery<findAllMembershipLog, findAllMembershipLogVariables>(GET_MEMBERSHIP_LOG, {
    variables: {
      query: {
        take: TAKE_ALL_ITEMS_COUNT,
        where: {
          user: {
            id: user.id,
          },
        },
      },
    },
  })

  const handleAddMembership = () => {
    setMembershipForms((forms) => [...forms, { isNew: true }])
    setSelectedPlatforms((platforms) => [...platforms, null])
  }

  const handleSelectPlatform = (index: number, value: PlatformName | null) => {
    setSelectedPlatforms((platforms) => platforms?.map((platformName, i) => (i === index ? value : platformName)))
  }

  const showFailedToast = () => toast.error('Failed to update membership')

  const membershipLogItems = membershipData?.findAllMembershipLog?.items
  const handleUpdateUserError = async (error: unknown) => {
    if (error instanceof Error) {
      if ('graphQLErrors' in error) {
        const gQLErr = error.graphQLErrors as ApolloError['graphQLErrors']
        if (gQLErr[0]?.message == 'Membership data is outdated, please try again') {
          toast.error('Membership data has been updated since the page was loaded. Reloading... Please try again')
          await refetchUser()
        } else {
          showFailedToast()
        }
      } else {
        showFailedToast()
      }
    } else {
      showFailedToast()
    }
  }

  const handleSubmitMembershipForm = async (values: MembershipInput) => {
    try {
      await updateUserMembershipByAdmin({
        variables: {
          updateUserMembershipByAdminInput: {
            ...values,
          },
          membershipUpdatedAt: new Date(user.membershipUpdatedAt ?? 0).toISOString(),
        },
      })
      await Promise.all([refetchUser(), refetchMembershipLogs()])

      toast.success('Membership updated successfully')
    } catch (error) {
      handleUpdateUserError(error)
    }
  }

  const renderMembershipLogsTable = () => {
    if (membershipLoading) {
      return <Loader />
    }

    if (!membershipLogItems?.length) {
      return <Typography data-testid="no-membership-logs">No logs found</Typography>
    }

    return (
      <Table
        css={classes.membershipLogsTable}
        hideHeader
        columns={[
          {
            title: 'IP Address',
            renderCell: (membershipLog) => <span>{membershipLog.log}</span>,
            flex: 1,
          },
        ]}
        items={membershipLogItems}
        wrapperMinWidth={350}
        data-testid="membership-logs"
      />
    )
  }

  const isEditAllowed = isAdmin || isMarketingTeamMember

  return (
    <section css={tabsClasses.container}>
      <div css={tabsClasses.form}>
        <Typography variant="title3" css={tabsClasses.title}>
          Memberships
        </Typography>
        <Divider />
        {platformsLoading || productsLoading ? (
          <Loader />
        ) : (
          <>
            {membershipForms.map((membershipForm, i) => (
              <MembershipForm
                key={i}
                userId={userId}
                index={i}
                isSubmitDisabled={updatingUserMembership}
                membership={membershipForm}
                membershipList={membership}
                platforms={platforms}
                products={products}
                selectedPlatforms={selectedPlatforms}
                onSelectPlatform={handleSelectPlatform}
                onSubmitMembershipForm={handleSubmitMembershipForm}
                isReadOnly={!isEditAllowed}
              />
            ))}
            <Button
              variant="outlined"
              color="blue"
              size="large"
              fullWidth
              disabled={!isEditAllowed || platforms.length <= membershipForms.length}
              onClick={handleAddMembership}
              data-testid="add-membership-button"
            >
              Add Membership <AddRoundedIcon />
            </Button>
          </>
        )}

        <Typography variant="title3" css={tabsClasses.title}>
          Activity Logs
        </Typography>
        <Divider />
        <Typography variant="title3" css={[tabsClasses.title, classes.secondaryTitle]}>
          Membership Logs
        </Typography>
        {renderMembershipLogsTable()}
      </div>
    </section>
  )
}
